<template>
    <!-- El contenido histórico de App.vue se ha movido aquí para la vista Admin -->
    <div id="admin-view">
        <!-- Reutilizamos la plantilla completa originalmente en App.vue -->
        <!-- Header y resto del layout copiados desde App.vue -->
        <div v-if="!isLoginPage" class="header">
            <div class="container">
                <div class="header-content">
                    <div>
                        <h1>📖 Editor de Historias - The Murder (Admin)</h1>
                        <p>Gestiona historias, tramas y chunks para el juego</p>
                    </div>
                    <div style="display: flex; gap: 10px; align-items: center">
                        <button
                            v-if="serverLock.isLocked"
                            class="btn btn-sm"
                            :class="serverLock.lockedBy === currentUser?.username ? 'btn-warning' : 'btn-danger'"
                            @click="toggleServerLock"
                            :disabled="serverLock.lockedBy !== currentUser?.username"
                            :title="
                                serverLock.lockedBy === currentUser?.username
                                    ? 'Click para desbloquear (tú bloqueaste el servidor)'
                                    : `Servidor bloqueado por ${serverLock.lockedBy} - Solo él/ella puede desbloquear`
                            "
                        >
                            🔒 {{ serverLock.lockedBy === currentUser?.username ? "Desbloq." : serverLock.lockedBy }}
                        </button>
                        <button
                            v-else
                            class="btn btn-sm btn-danger"
                            @click="toggleServerLock"
                            title="Bloquear guardado en servidor para TODOS los usuarios (incluyéndote)"
                        >
                            🔓 Bloquear
                        </button>

                        <span class="user-info">👤 {{ currentUser?.username }}</span>

                        <button class="btn btn-sm btn-secondary" @click="handleLogout">🚪 Salir</button>
                        <button
                            class="theme-toggle"
                            @click="toggleTheme"
                            :title="isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'"
                        >
                            <span v-if="isDark">☀️</span>
                            <span v-else>🌙</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Banner de sincronización -->
        <div
            v-if="!isLoginPage && !isLoading"
            class="sync-banner"
            :class="{
                'sync-banner-synced': !hasConflicts,
                'sync-banner-conflict': hasConflicts,
            }"
        >
            <div class="sync-banner-content px-4">
                <div class="sync-info">
                    <span v-if="isSyncing" class="sync-status">⏳ Verificando sincronización...</span>
                    <span v-else-if="hasConflicts" class="sync-status"
                        >⚠️ {{ filesWithDifferences }} archivo(s) con diferencias respecto al servidor</span
                    >
                    <span v-else class="sync-status">✅ Todos los archivos sincronizados</span>
                </div>
                <div style="display: flex; gap: 8px">
                    <button
                        class="btn btn-sm"
                        :class="hasConflicts ? 'btn-warning' : 'btn-outline-secondary'"
                        @click="openSyncModal"
                        :disabled="isSyncing"
                    >
                        <span v-if="hasConflicts">⚠️ Ver diferencias</span>
                        <span v-else>📊 Ver estado</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Contenido principal solo cuando NO es login -->
        <div v-if="!isLoginPage" class="container">
            <!-- Sección de guardados en servidor -->
            <div class="saves-section">
                <div class="saves-header">
                    <h3>💾 Historial de Guardados en Servidor</h3>

                    <div style="display: flex; flex-direction: column; gap: 5px; align-items: flex-end">
                        <button
                            class="btn btn-success"
                            @click="saveToServer"
                            :disabled="savingToServer || serverLock.isLocked"
                            :title="serverLock.isLocked ? `Servidor bloqueado por ${serverLock.lockedBy}` : ''"
                        >
                            <span v-if="savingToServer">⏳ Guardando...</span>
                            <span v-else>💾 Guardar en Servidor</span>
                        </button>
                        <small v-if="serverLock.isLocked" class="text-danger">🔒 Bloqueado por {{ serverLock.lockedBy }}</small>
                    </div>
                </div>

                <div v-if="showSavesHistory" class="saves-history">
                    <button class="btn btn-sm btn-secondary" @click="showSavesHistory = false">✕ Cerrar</button>
                    <div v-if="loadingSaves" class="text-center">⏳ Cargando guardados...</div>
                    <div v-else-if="saves.length === 0" class="empty-state">No hay guardados anteriores</div>
                    <div v-else class="saves-list">
                        <div
                            v-for="save in saves"
                            :key="save.username + '-' + save.timestamp"
                            class="save-item"
                            :class="{'other-user': !save.isOwner}"
                        >
                            <div class="save-info">
                                <div>
                                    <strong>📅 {{ save.date }}</strong>
                                </div>
                                <div class="user-badge" :class="{'own-save': save.isOwner}">
                                    👤 {{ save.username }} <span v-if="save.isOwner" class="owner-badge">✓</span>
                                </div>
                            </div>
                            <div class="save-actions">
                                <button class="btn btn-sm btn-primary" @click="loadFromServer(save.timestamp, save.username)">
                                    📥 Cargar
                                </button>
                                <button
                                    class="btn btn-sm btn-success"
                                    @click="downloadSaveAsZip(save.timestamp, save.username)"
                                    title="Descargar ZIP"
                                >
                                    📦 ZIP
                                </button>
                                <button
                                    class="btn btn-sm btn-danger"
                                    @click="deleteSaveFromServer(save.timestamp, save.username)"
                                    :disabled="!save.isOwner"
                                    :title="save.isOwner ? 'Eliminar' : 'Solo puedes borrar tus propios guardados'"
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <button v-if="!showSavesHistory" class="btn btn-sm btn-info" @click="loadSavesList">
                    📋 Ver Historial ({{ saves.length }})
                </button>
            </div>

            <div class="actions-bar">
                <button class="btn btn-info" @click="showLoadJsonMenu = !showLoadJsonMenu">📁 Cargar JSON</button>
                <button class="btn btn-primary" @click="downloadAllServerJSON">📦 Descargar Todos los JSON DEL SERVIDOR</button>
                <button class="btn btn-warning" @click="downloadAllJSON">📦 Descargar Todos los JSON LOCALES</button>
                <button class="btn btn-danger" @click="clearData">🗑️ BORRAR TODOS LOS DATOS EN LOCAL</button>
            </div>

            <!-- Menú de carga de JSON -->
            <div v-if="showLoadJsonMenu" class="load-json-menu">
                <h4>📁 Selecciona el tipo de archivo a cargar:</h4>
                <div class="json-type-buttons">
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('afinidades')">❤️ Afinidades</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('apellidos')">📛 Apellidos</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('bot_vinculos')">🤖 Bot Vínculos</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('clase_ocupaciones')">
                        🏷️ Clase Ocupaciones
                    </button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('clase_rasgos_economicos')">
                        🏷️ Clase Rasgos Econ.
                    </button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('clase_vinculos')">🔗 Clase Vínculos</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('colecciones')">🧩 Colecciones</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('descartes_tipos_metodos')">
                        🗑️ Descartes Tipos Métodos
                    </button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('epilogos')">✒️ Epílogos</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('escenarios')">🗺️ Escenarios</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('historias')">📖 Historias</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('metodos')">🔪 Métodos</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('objetos')">📦 Objetos</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('ocupaciones')">💼 Ocupaciones</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('personajes')">🎭 Personajes</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('referencias')">📚 Referencias</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('rasgos_clase')">🎭 Rasgos Clase</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('rasgos_economicos')">
                        💰 Rasgos Económicos
                    </button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('rasgos_fisicos')">💪 Rasgos Físicos</button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('rasgos_personalidad')">
                        🧠 Rasgos Personalidad
                    </button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('textos_coincidencias')">
                        🤝 Textos Coincidencias
                    </button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('textos_conjunciones_adversativas')">
                        ↔️ Textos Conjunciones Adversativas
                    </button>
                    <button class="btn btn-sm btn-outline" @click="triggerFileUpload('vinculos')">👥 Vínculos</button>
                </div>
                <input ref="fileInput" type="file" accept=".json" style="display: none" @change="handleFileUpload" />
            </div>

            <!-- Modal de diff -->
            <JsonDiffModal
                :is-open="diffModalOpen"
                :changes="currentChanges"
                :data-type-name="currentDataTypeName"
                @close="closeDiffModal"
                @apply="applyChanges"
            />

            <!-- Modal de sincronización -->
            <SyncModal
                :is-open="syncModalOpen"
                :sync-results="syncResults"
                :is-syncing="isSyncing"
                :has-conflicts="hasConflicts"
                @close="closeSyncModal"
                @accept-server-version="acceptServerVersion"
                @accept-all-server-versions="acceptAllServerVersions"
                @accept-individual-change="acceptSingleChange"
                @accept-selected-changes="acceptSelectedChanges"
            />

            <div v-if="isLoading" class="empty-state" style="margin-top: 40px; text-align: center">
                <h2>⏳ Cargando datos iniciales...</h2>
            </div>

            <!-- Pestañas de navegación -->
            <div v-if="!isLoading" class="main-tabs">
                <router-link to="/historias" class="tab-btn" active-class="active">📖 Historias</router-link>
                <router-link to="/metodos" class="tab-btn" active-class="active">🔪 Métodos</router-link>
                <router-link to="/escenarios" class="tab-btn" active-class="active">🗺️ Escenarios</router-link>
                <router-link to="/datos" class="tab-btn" active-class="active">🗂️ Datos del Juego</router-link>
                <router-link to="/bot-vinculos" class="tab-btn" active-class="active">🤖 Bot Vínculos</router-link>
                <router-link to="/casos" class="tab-btn" active-class="active">🎲 Casos</router-link>

                <!-- Consolidated Envivo tabs to save space -->
                <router-link :to="envRoute('partidas')" class="tab-btn" :class="{active: isSectionActive('partidas')}"
                    >🎮 Partidas</router-link
                >
                <router-link :to="envRoute('usuarios')" class="tab-btn" :class="{active: isSectionActive('usuarios')}"
                    >👤 Usuarios</router-link
                >
                <router-link :to="envRoute('backups')" class="tab-btn" :class="{active: isSectionActive('backups')}"
                    >💾 Backups</router-link
                >

                <router-link to="/game-settings" active-class="" class="tab-btn">⚙️ Game Settings</router-link>
            </div>

            <!-- Envivo / Envivo DEV subtabs (select instance) -->
            <div v-if="isEnvSectionActive()" class="env-subtabs mt-2 mb-3">
                <div class="btn-group" role="group" aria-label="Env selector">
                    <button
                        type="button"
                        class="btn btn-sm"
                        :class="envMode === 'app' ? 'btn-primary' : 'btn-outline-secondary'"
                        @click="switchEnv('app')"
                    >
                        Envivo
                    </button>
                    <button
                        type="button"
                        class="btn btn-sm"
                        :class="envMode === 'dev' ? 'btn-primary' : 'btn-outline-secondary'"
                        @click="switchEnv('dev')"
                    >
                        Envivo DEV
                    </button>
                </div>
            </div>

            <!-- Router View para rutas autenticadas -->
            <router-view v-if="!isLoading" />
        </div>

        <!-- Router View para login (cuando NO está autenticado) -->
        <div v-else-if="isLoginPage">
            <router-view />
        </div>

        <!-- Contenedor de toasts para notificaciones de sincronización -->
        <div class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 11000">
            <div id="sync-toast" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
                <div class="toast-header">
                    <strong class="me-auto">🔄 Sincronización</strong>
                    <small class="text-muted">Ahora</small>
                    <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close">X</button>
                </div>
                <div class="toast-body" id="sync-toast-body">Datos actualizados</div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, computed, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import JSZip from "jszip";
import {clearAllData, KEYS, saveToIndexedDB} from "../services/indexedDBService.js";
import api from "../services/api.js";
import JsonDiffModal from "../components/JsonDiffModal.vue";
import SyncModal from "../components/SyncModal.vue";
import {useSyncChecker} from "../composables/useSyncChecker.js";
import {
    useHistorias,
    useMetodos,
    useEscenarios,
    useReferencias,
    useVinculos,
    useBotVinculos,
    useClaseVinculos,
    useObjetos,
    useRasgosPersonalidad,
    useRasgosFisicos,
    useRasgosClase,
    useRasgosEconomicos,
    useClaseRasgosEconomicos,
    useAfinidades,
    useOcupaciones,
    useClaseOcupaciones,
    useApellidos,
    useEpilogos,
    usePersonajes,
    useDescartesTiposMetodos,
    useTextosCoincidencias,
    useTextosConjuncionesAdversativas,
    useColecciones,
    useGameData,
} from "../composables/useGameData.js";

import {useColeccionesSync} from "../composables/useColeccionesSync.js";
import {useEscenariosSync} from "../composables/useEscenariosSync.js";

const {syncColecciones} = useColeccionesSync();
const {syncEscenarios} = useEscenariosSync();

const route = useRoute();
const router = useRouter();

const currentUser = ref(null);
const serverLock = ref({isLocked: false, lockedBy: null, lockedAt: null});
const isLoginPage = computed(() => route.path === "/login");
const isDark = ref(false);
const showLoadJsonMenu = ref(false);
const fileInput = ref(null);
const currentUploadType = ref(null);
const diffModalOpen = ref(false);
const currentChanges = ref([]);
const currentDataTypeName = ref("");
const pendingNewData = ref(null);

const {isSyncing, syncResults, hasConflicts, checkSync} = useSyncChecker();
const syncModalOpen = ref(false);

const filesWithDifferences = computed(() => {
    return syncResults.value ? syncResults.value.filter(r => r.status === "different").length : 0;
});

const isLoading = ref(true);
const saves = ref([]);
const showSavesHistory = ref(false);
const loadingSaves = ref(false);
const savingToServer = ref(false);

const {historias, updateHistorias} = useHistorias();
const {metodos, updateMetodos} = useMetodos();
const {escenarios, updateEscenarios} = useEscenarios();
const {referencias, updateReferencias} = useReferencias();
const {vinculos, updateVinculos} = useVinculos();
const {botVinculos, updateBotVinculos} = useBotVinculos();
const {claseVinculos, updateClaseVinculos} = useClaseVinculos();
const {objetos, updateObjetos} = useObjetos();
const {rasgosPersonalidad, updateRasgosPersonalidad} = useRasgosPersonalidad();
const {rasgosFisicos, updateRasgosFisicos} = useRasgosFisicos();
const {rasgosClase, updateRasgosClase} = useRasgosClase();
const {rasgosEconomicos, updateRasgosEconomicos} = useRasgosEconomicos();
const {claseRasgosEconomicos, updateClaseRasgosEconomicos} = useClaseRasgosEconomicos();
const {afinidades, updateAfinidades} = useAfinidades();
const {ocupaciones, updateOcupaciones} = useOcupaciones();
const {claseOcupaciones, updateClaseOcupaciones} = useClaseOcupaciones();
const {apellidos, updateApellidos} = useApellidos();
const {epilogos, updateEpilogos} = useEpilogos();
const {personajes, updatePersonajes} = usePersonajes();
const {descartesTiposMetodos, updateDescartesTiposMetodos} = useDescartesTiposMetodos();
const {textosCoincidencias, updateTextosCoincidencias} = useTextosCoincidencias();
const {textosConjuncionesAdversativas, updateTextosConjuncionesAdversativas} = useTextosConjuncionesAdversativas();
const {colecciones, updateColecciones} = useColecciones();

const {loadAllGameData, refreshFromIndexedDB} = useGameData();

// Envivo / Envivo DEV selector for compact tabs UI
const envMode = ref(route.path.startsWith("/dev") ? "dev" : "app");
const envPrefix = computed(() => (envMode.value === "dev" ? "/dev" : "/app"));

function envRoute(section) {
    return `${envPrefix.value}/${section}`;
}

function getCurrentEnvSection() {
    const m = route.path.match(/(partidas|usuarios|backups)/);
    return m ? m[1] : "partidas";
}

function switchEnv(mode) {
    if (envMode.value === mode) return;
    const section = getCurrentEnvSection();
    envMode.value = mode;
    router.push(envRoute(section)).catch(() => {});
}

function isSectionActive(section) {
    return route.path.startsWith(envPrefix.value) && route.path.includes(`/${section}`);
}

function isEnvSectionActive() {
    return route.path.startsWith("/app") || route.path.startsWith("/dev");
}

// Keep envMode in sync with route changes
watch(
    () => route.path,
    p => {
        if (p.startsWith("/dev")) envMode.value = "dev";
        else if (p.startsWith("/app")) envMode.value = "app";
    },
);

let lockRefreshInterval = null;

function loadThemePreference() {
    const savedTheme = localStorage.getItem("theme");
    isDark.value = savedTheme === "dark";
    applyTheme();
}

function applyTheme() {
    const htmlElement = document.documentElement;
    if (isDark.value) {
        htmlElement.setAttribute("data-bs-theme", "dark");
    } else {
        htmlElement.setAttribute("data-bs-theme", "light");
    }
}

function toggleTheme() {
    isDark.value = !isDark.value;
    localStorage.setItem("theme", isDark.value ? "dark" : "light");
    applyTheme();
}

watch(isDark, () => {
    applyTheme();
});

async function refreshAllViews() {
    await refreshFromIndexedDB();
}

async function loadServerLock() {
    try {
        const response = await fetch("/api/server-lock", {
            credentials: "include",
        });
        if (response.ok) {
            serverLock.value = await response.json();
        }
    } catch (error) {
        console.error("Error cargando estado de bloqueo:", error);
    }
}

async function toggleServerLock() {
    try {
        const endpoint = serverLock.value.isLocked ? "/api/server-lock/unlock" : "/api/server-lock/lock";
        const response = await fetch(endpoint, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
        });

        const data = await response.json();

        if (response.ok) {
            await loadServerLock();

            const message = serverLock.value.isLocked
                ? "🔒 Servidor bloqueado por ti.\n\nNINGÚN usuario (incluyéndote) podrá guardar hasta que desbloquees."
                : "🔓 Servidor desbloqueado.\n\nTodos los usuarios pueden guardar ahora.";

            alert(message);
        } else {
            alert(`❌ ${data.error || "Error al cambiar el bloqueo"}`);
        }
    } catch (error) {
        console.error("Error cambiando bloqueo:", error);
        alert("❌ Error al cambiar el estado del bloqueo");
    }
}

function handleLogout() {
    api.logout();
    currentUser.value = null;
    router.push("/login");
}

async function loadSavesList() {
    loadingSaves.value = true;
    showSavesHistory.value = true;
    try {
        saves.value = await api.listSaves();
    } catch (error) {
        console.error("Error cargando lista de guardados:", error);
        alert("Error al cargar lista de guardados: " + error.message);
    } finally {
        loadingSaves.value = false;
    }
}

async function saveToServer() {
    if (serverLock.value.isLocked && serverLock.value.lockedBy !== currentUser.value?.username) {
        alert(`❌ El servidor está bloqueado por ${serverLock.value.lockedBy}.\n\nNo puedes guardar hasta que se desbloquee.`);
        return;
    }

    if (
        !confirm(
            "⚠️ ¿Guardar el estado actual en el servidor?\n\nEsta acción:\n1. Guardará una copia en tu carpeta personal\n2. SUSTITUIRÁ los archivos JSON del servidor por esta versión\n\n¿Estás seguro de continuar?",
        )
    )
        return;

    savingToServer.value = true;
    try {
        const dataToSave = {
            historias: historias.value,
            metodos: metodos.value,
            referencias: referencias.value,
            vinculos: vinculos.value,
            bot_vinculos: botVinculos.value,
            clase_vinculos: claseVinculos.value,
            clase_rasgos_economicos: claseRasgosEconomicos.value,
            objetos: objetos.value,
            rasgos_personalidad: rasgosPersonalidad.value,
            rasgos_fisicos: rasgosFisicos.value,
            rasgos_clase: rasgosClase.value,
            rasgos_economicos: rasgosEconomicos.value,
            afinidades: afinidades.value,
            ocupaciones: ocupaciones.value,
            apellidos: apellidos.value,
            escenarios: escenarios.value,
            personajes: personajes.value,
            colecciones: colecciones.value,
            clase_ocupaciones: claseOcupaciones.value,
            epilogos: epilogos.value,
            descartes_tipos_metodos: descartesTiposMetodos.value,
            textos_coincidencias: textosCoincidencias.value,
            textos_conjunciones_adversativas: textosConjuncionesAdversativas.value,
        };

        // sincronizamos con el backoffice de la app y dev coleccioes y escenarios
        const result = await api.saveToServer(dataToSave);
        const apps = ["dev", "app"];
        for (const app of apps) {
            await syncColecciones(app);
            await syncEscenarios(app);
        }

        alert(`✅ Guardado exitoso:\n- Copia personal: ${result.timestamp}\n- Archivos del servidor actualizados`);

        await loadSavesList();
        await checkSynchronization();
    } catch (error) {
        if (error.response?.status === 423) {
            const data = error.response.data;
            alert(`❌ ${data.message || "El servidor está bloqueado"}\n\nBloqueado por: ${data.lockedBy}`);
            await loadServerLock();
        } else {
            console.error("Error guardando en servidor:", error);
            alert("Error al guardar en servidor: " + error.message);
        }
    } finally {
        savingToServer.value = false;
    }
}

async function loadFromServer(timestamp, username) {
    const userInfo = username ? ` de ${username}` : "";
    if (!confirm(`¿Cargar el guardado${userInfo} del ${timestamp}? Esto reemplazará los datos actuales.`)) return;

    try {
        const data = await api.loadSave(timestamp, username);

        await updateHistorias(data.historias || {historias: []});
        await updateMetodos(data.metodos || {metodos: []});
        await updateReferencias(data.referencias || {referencias: []});
        await updateVinculos(data.vinculos || {vinculos: []});
        await updateBotVinculos(data.bot_vinculos || {sabes_algo_mas: {}, bot_vinculos: []});
        await updateClaseVinculos(data.clase_vinculos || {clase_vinculos: []});
        await updateObjetos(data.objetos || {objetos: []});
        await updateRasgosPersonalidad(data.rasgos_personalidad || {rasgos_personalidad: []});
        await updateRasgosFisicos(data.rasgos_fisicos || {rasgos_fisicos: []});
        await updateRasgosClase(data.rasgos_clase || {rasgos_clase: []});
        await updateRasgosEconomicos(data.rasgos_economicos || {rasgos_economicos: []});
        await updateClaseRasgosEconomicos(data.clase_rasgos_economicos || {clase_rasgos_economicos: []});
        await updateAfinidades(data.afinidades || {afinidades: []});
        await updateOcupaciones(data.ocupaciones || {ocupaciones: []});
        await updateClaseOcupaciones(data.clase_ocupaciones || {clase_ocupaciones: []});
        await updateApellidos(data.apellidos || {apellidos: []});
        await updateEpilogos(
            data.epilogos || {
                epilogos: {
                    asesino: {hasganado: [], hasperdido: []},
                    inocente: {
                        habeisganado: {hasacertado: [], hasfallado: []},
                        habeisperdido: {hasacertado: [], hasfallado: []},
                    },
                },
            },
        );
        await updateEscenarios(data.escenarios || {escenarios: []});
        await updatePersonajes(data.personajes || {personajes: []});
        await updateColecciones(data.colecciones || {colecciones: []});
        await updateDescartesTiposMetodos(data.descartes_tipos_metodos || {descartes_tipos_metodos: []});
        await updateTextosCoincidencias(
            data.textos_coincidencias || {
                textos_coincidencias: {
                    frases_coincidencia: [],
                    coletillas_frases_coincidencia: [],
                    frases_coincidencia_bot: [],
                    coletillas_frases_coincidencia_bot: [],
                },
            },
        );
        await updateTextosConjuncionesAdversativas(
            data.textos_conjunciones_adversativas || {textos_conjunciones_adversativas: {conjunciones_adversativas: []}},
        );

        console.log("🔄 Verificando sincronización después de cargar guardado...");
        await checkSynchronization();

        if (showSavesHistory.value) {
            await loadSavesList();
        }

        alert("✅ Datos cargados correctamente");
    } catch (error) {
        console.error("Error cargando desde servidor:", error);
        alert("Error al cargar desde servidor: " + error.message);
    }
}

async function deleteSaveFromServer(timestamp, username) {
    if (!confirm(`¿Eliminar el guardado del ${timestamp}?`)) return;

    try {
        await api.deleteSave(timestamp, username);
        alert("✅ Guardado eliminado");
        await loadSavesList();
    } catch (error) {
        console.error("Error eliminando guardado:", error);
        alert("Error al eliminar guardado: " + error.message);
    }
}

async function downloadSaveAsZip(timestamp, username) {
    try {
        const blob = await api.downloadSaveAsZip(timestamp, username);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const saveUsername = username || currentUser.value?.username || "usuario";
        a.download = `iiev_${saveUsername}_${timestamp}.json.zip`;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        console.error("Error descargando ZIP:", error);
        alert("Error al descargar ZIP: " + error.message);
    }
}

async function downloadAllServerJSON() {
    try {
        const blob = await api.downloadLastSaveAsZip();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0];
        a.download = `iiev_data_server_${timestamp}.zip`;
        document.body.appendChild(a);
        a.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    } catch (error) {
        console.error("Error descargando todos los JSON del servidor:", error);
        alert("Error al descargar todos los JSON del servidor: " + error.message);
    }
}

async function downloadAllJSON() {
    const zip = new JSZip();
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T")[0];

    zip.file("historias.json", JSON.stringify(historias.value, null, 2));
    zip.file("metodos.json", JSON.stringify(metodos.value, null, 2));
    zip.file("referencias.json", JSON.stringify(referencias.value, null, 2));
    zip.file("vinculos.json", JSON.stringify(vinculos.value, null, 2));
    zip.file("bot_vinculos.json", JSON.stringify(botVinculos.value, null, 2));
    zip.file("clase_vinculos.json", JSON.stringify(claseVinculos.value, null, 2));
    zip.file("objetos.json", JSON.stringify(objetos.value, null, 2));
    zip.file("rasgos_personalidad.json", JSON.stringify(rasgosPersonalidad.value, null, 2));
    zip.file("rasgos_fisicos.json", JSON.stringify(rasgosFisicos.value, null, 2));
    zip.file("rasgos_clase.json", JSON.stringify(rasgosClase.value, null, 2));
    zip.file("rasgos_economicos.json", JSON.stringify(rasgosEconomicos.value, null, 2));
    zip.file("clase_rasgos_economicos.json", JSON.stringify(claseRasgosEconomicos.value, null, 2));
    zip.file("afinidades.json", JSON.stringify(afinidades.value, null, 2));
    zip.file("ocupaciones.json", JSON.stringify(ocupaciones.value, null, 2));
    zip.file("clase_ocupaciones.json", JSON.stringify(claseOcupaciones.value, null, 2));
    zip.file("apellidos.json", JSON.stringify(apellidos.value, null, 2));
    zip.file("epilogos.json", JSON.stringify(epilogos.value, null, 2));
    zip.file("escenarios.json", JSON.stringify(escenarios.value, null, 2));
    zip.file("personajes.json", JSON.stringify(personajes.value, null, 2));
    zip.file("colecciones.json", JSON.stringify(colecciones.value, null, 2));
    zip.file("descartes_tipos_metodos.json", JSON.stringify(descartesTiposMetodos.value, null, 2));
    zip.file("textos_coincidencias.json", JSON.stringify(textosCoincidencias.value, null, 2));
    zip.file("textos_conjunciones_adversativas.json", JSON.stringify(textosConjuncionesAdversativas.value, null, 2));

    const zipBlob = await zip.generateAsync({type: "blob"});
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `iiev_data_local_${timestamp}.zip`;
    a.click();
    URL.revokeObjectURL(url);
}

const dataTypeConfig = {
    historias: {key: "historias", ref: historias, update: updateHistorias, label: "Historias", dbKey: KEYS.HISTORIAS},
    metodos: {key: "metodos", ref: metodos, update: updateMetodos, label: "Métodos", dbKey: KEYS.METODOS},
    escenarios: {key: "escenarios", ref: escenarios, update: updateEscenarios, label: "Escenarios", dbKey: KEYS.ESCENARIOS},
    referencias: {key: "referencias", ref: referencias, update: updateReferencias, label: "Referencias", dbKey: KEYS.REFERENCIAS},
    vinculos: {key: "vinculos", ref: vinculos, update: updateVinculos, label: "Vínculos", dbKey: KEYS.VINCULOS},
    bot_vinculos: {
        key: "bot_vinculos",
        ref: botVinculos,
        update: updateBotVinculos,
        label: "Bot Vínculos",
        dbKey: KEYS.BOT_VINCULOS,
    },
    clase_vinculos: {
        key: "clase_vinculos",
        ref: claseVinculos,
        update: updateClaseVinculos,
        label: "Clase Vínculos",
        dbKey: KEYS.CLASE_VINCULOS,
    },
    objetos: {key: "objetos", ref: objetos, update: updateObjetos, label: "Objetos", dbKey: KEYS.OBJETOS},
    rasgos_personalidad: {
        key: "rasgos_personalidad",
        ref: rasgosPersonalidad,
        update: updateRasgosPersonalidad,
        label: "Rasgos Personalidad",
        dbKey: KEYS.RASGOS_PERSONALIDAD,
    },
    rasgos_fisicos: {
        key: "rasgos_fisicos",
        ref: rasgosFisicos,
        update: updateRasgosFisicos,
        label: "Rasgos Físicos",
        dbKey: KEYS.RASGOS_FISICOS,
    },
    rasgos_clase: {
        key: "rasgos_clase",
        ref: rasgosClase,
        update: updateRasgosClase,
        label: "Rasgos Clase",
        dbKey: KEYS.RASGOS_CLASE,
    },
    rasgos_economicos: {
        key: "rasgos_economicos",
        ref: rasgosEconomicos,
        update: updateRasgosEconomicos,
        label: "Rasgos Económicos",
        dbKey: KEYS.RASGOS_ECONOMICOS,
    },
    clase_rasgos_economicos: {
        key: "clase_rasgos_economicos",
        ref: claseRasgosEconomicos,
        update: updateClaseRasgosEconomicos,
        label: "Clase Rasgos Económicos",
        dbKey: KEYS.CLASE_RASGOS_ECONOMICOS,
    },
    afinidades: {key: "afinidades", ref: afinidades, update: updateAfinidades, label: "Afinidades", dbKey: KEYS.AFINIDADES},
    ocupaciones: {key: "ocupaciones", ref: ocupaciones, update: updateOcupaciones, label: "Ocupaciones", dbKey: KEYS.OCUPACIONES},
    clase_ocupaciones: {
        key: "clase_ocupaciones",
        ref: claseOcupaciones,
        update: updateClaseOcupaciones,
        label: "Clase Ocupaciones",
        dbKey: KEYS.CLASE_OCUPACIONES,
    },
    apellidos: {key: "apellidos", ref: apellidos, update: updateApellidos, label: "Apellidos", dbKey: KEYS.APELLIDOS},
    epilogos: {key: "epilogos", ref: epilogos, update: updateEpilogos, label: "Epílogos", dbKey: KEYS.EPILOGOS},
    personajes: {key: "personajes", ref: personajes, update: updatePersonajes, label: "Personajes", dbKey: KEYS.PERSONAJES},
    colecciones: {key: "colecciones", ref: colecciones, update: updateColecciones, label: "Colecciones", dbKey: KEYS.COLECCIONES},
    descartes_tipos_metodos: {
        key: "descartes_tipos_metodos",
        ref: descartesTiposMetodos,
        update: updateDescartesTiposMetodos,
        label: "Descartes Tipos Métodos",
        dbKey: KEYS.DESCARTES_TIPOS_METODOS,
    },
    textos_coincidencias: {
        key: "textos_coincidencias",
        ref: textosCoincidencias,
        update: updateTextosCoincidencias,
        label: "Textos Coincidencias",
        dbKey: KEYS.TEXTOS_COINCIDENCIAS,
    },
    textos_conjunciones_adversativas: {
        key: "textos_conjunciones_adversativas",
        ref: textosConjuncionesAdversativas,
        update: updateTextosConjuncionesAdversativas,
        label: "Textos Conjunciones Adversativas",
        dbKey: KEYS.TEXTOS_CONJUNCIONES_ADVERSATIVAS,
    },
};

function triggerFileUpload(type) {
    currentUploadType.value = type;
    fileInput.value.click();
}

async function handleFileUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    try {
        const text = await file.text();
        const newData = JSON.parse(text);

        const config = dataTypeConfig[currentUploadType.value];
        if (!config) {
            alert("Tipo de datos no reconocido");
            return;
        }

        const confirmReplace = confirm(`¿Reemplazar ${config.label} completo con el archivo seleccionado?`);
        if (!confirmReplace) return;

        await config.update(newData);
        alert(`✅ ${config.label} actualizado correctamente`);
        showLoadJsonMenu.value = false;
    } catch (error) {
        console.error("Error cargando archivo:", error);
        alert("❌ Error al cargar el archivo: " + error.message);
    } finally {
        event.target.value = "";
    }
}

function closeDiffModal() {
    diffModalOpen.value = false;
    currentChanges.value = [];
    currentDataTypeName.value = "";
    pendingNewData.value = null;
}

async function applyChanges(acceptedChanges) {
    if (!acceptedChanges || acceptedChanges.length === 0) {
        closeDiffModal();
        return;
    }

    let config = pendingNewData.value?.config;
    let baseData = pendingNewData.value?.currentData;

    if (!config) {
        config =
            dataTypeConfig[currentUploadType.value] ||
            Object.values(dataTypeConfig).find(c => c.label === currentDataTypeName.value);
    }

    if (!config) {
        alert("❌ No se pudo determinar el tipo de datos para aplicar cambios");
        closeDiffModal();
        return;
    }

    if (!baseData) {
        baseData = JSON.parse(JSON.stringify(config.ref.value || {}));
    }

    const currentData = JSON.parse(JSON.stringify(baseData));
    const mainKey = config.key;

    if (currentData[mainKey] === undefined) {
        currentData[mainKey] = [];
    }

    acceptedChanges.forEach(change => {
        if (Array.isArray(currentData[mainKey])) {
            applyChangeToData(currentData[mainKey], change);
        } else {
            applyChangeToObject(currentData, change, mainKey);
        }
    });

    const updatedData = currentData;

    await config.update(updatedData);
    await refreshAllViews();

    console.log(`✅ Datos actualizados y guardados en IndexedDB para ${config.label}`);

    alert(`✅ Se aplicaron ${acceptedChanges.length} cambios a ${config.label}`);
    closeDiffModal();
    showLoadJsonMenu.value = false;
}

function applyChangeToData(data, change) {
    const {field, type, newValue, id} = change;

    let changeId = id;

    if (!changeId && typeof field === "string") {
        const match = field.match(/\[id=([^\]]+)\]/);
        if (match) changeId = match[1];
    }

    if (!field || field === change.itemName) {
        if (type === "added" && newValue !== null) {
            data.push(newValue);
        } else if (type === "removed") {
            const index = data.findIndex(item => String(getItemId(item)) === String(changeId));
            if (index !== -1) {
                data.splice(index, 1);
            }
        } else if (type === "modified") {
            const index = data.findIndex(item => String(getItemId(item)) === String(changeId));
            if (index !== -1) {
                data[index] = newValue;
            }
        }
        return;
    }

    const parts = field.split(/\.|\[|\]/).filter(part => part !== "");

    if (parts.length === 0) {
        if (type === "added" && newValue !== null) {
            data.push(newValue);
        } else if (type === "removed") {
            const index = data.findIndex(item => String(getItemId(item)) === String(changeId));
            if (index !== -1) {
                data.splice(index, 1);
            }
        } else if (type === "modified") {
            const index = data.findIndex(item => String(getItemId(item)) === String(changeId));
            if (index !== -1) {
                data[index] = newValue;
            }
        }
        return;
    }

    const itemIndex = data.findIndex(item => String(getItemId(item)) === String(changeId));
    if (itemIndex === -1) return;

    if (parts.length === 1 && String(parts[0]).startsWith("id=")) {
        if (type === "removed") {
            data.splice(itemIndex, 1);
        } else if (type === "added") {
            if (newValue != null) data.push(newValue);
        } else if (type === "modified") {
            data[itemIndex] = newValue;
        }
        return;
    }

    let current = data[itemIndex];

    for (let i = 0; i < parts.length - 1; i++) {
        const part = parts[i];

        if (part.startsWith("id=")) {
            const nestedId = part.substring(3);
            if (Array.isArray(current)) {
                const index = current.findIndex(item => String(getItemId(item)) === nestedId);
                if (index !== -1) {
                    current = current[index];
                } else {
                    return;
                }
            } else if (current && typeof current === "object") {
                if (String(getItemId(current)) !== nestedId) {
                    return;
                }
            } else {
                return;
            }
        } else if (!isNaN(part)) {
            if (Array.isArray(current)) {
                current = current[parseInt(part)];
            } else if (current && typeof current === "object" && part in current) {
                current = current[part];
            } else {
                return;
            }
        } else {
            if (!current[part]) {
                current[part] = {};
            }
            current = current[part];
        }
    }

    const lastPart = parts[parts.length - 1];

    if (lastPart.startsWith("id=")) {
        const nestedId = lastPart.substring(3);
        if (!Array.isArray(current)) return;

        const index = current.findIndex(item => String(getItemId(item)) === String(nestedId));

        if (type === "removed") {
            if (index !== -1) current.splice(index, 1);
            return;
        }

        if (type === "added") {
            if (index === -1 && newValue != null) current.push(newValue);
            return;
        }

        if (type === "modified" && index !== -1) {
            current[index] = newValue;
        }
    } else {
        if (Array.isArray(current) && !isNaN(lastPart)) {
            const index = parseInt(lastPart);
            if (type === "added" || type === "modified") {
                current[index] = newValue;
            } else if (type === "removed") {
                current.splice(index, 1);
            }
        } else if (!Array.isArray(current)) {
            if (type === "added") {
                current[lastPart] = newValue;
            } else if (type === "removed") {
                delete current[lastPart];
            } else if (type === "modified") {
                current[lastPart] = newValue;
            }
        }
    }
}

function parseFieldTokens(field) {
    if (!field) return [];

    const tokens = [];
    const dotParts = field.split(".");

    dotParts.forEach(part => {
        const baseMatch = part.match(/^([^\[\]]+)/);
        if (baseMatch && baseMatch[1]) {
            tokens.push(baseMatch[1]);
        }

        const bracketRegex = /\[([^\]]+)\]/g;
        let match;
        while ((match = bracketRegex.exec(part)) !== null) {
            tokens.push(match[1]);
        }
    });

    return tokens;
}

function applyChangeToObject(wrapperData, change, mainKey) {
    const {field, type, newValue} = change;
    if (!field) return;

    const tokens = parseFieldTokens(field);
    if (tokens.length === 0) return;

    if (tokens[0] === mainKey) {
        tokens.shift();
    }
    if (tokens.length === 0) return;

    const root = wrapperData[mainKey] ?? wrapperData;
    let current = root;

    for (let i = 0; i < tokens.length - 1; i++) {
        const token = tokens[i];
        const next = tokens[i + 1];

        if (token.startsWith("id=")) {
            const nestedId = token.substring(3);
            if (!Array.isArray(current)) return;
            const idx = current.findIndex(item => String(getItemId(item)) === nestedId);
            if (idx === -1) return;
            current = current[idx];
            continue;
        }

        const isIndex = /^\d+$/.test(token);
        if (isIndex) {
            const idx = Number(token);
            if (!Array.isArray(current)) return;
            if (current[idx] === undefined) {
                current[idx] = /^\d+$/.test(next) ? [] : {};
            }
            current = current[idx];
            continue;
        }

        if (current[token] === undefined || current[token] === null) {
            current[token] = /^\d+$/.test(next) ? [] : {};
        }
        current = current[token];
    }

    const lastToken = tokens[tokens.length - 1];

    if (lastToken.startsWith("id=")) {
        const nestedId = lastToken.substring(3);
        if (!Array.isArray(current)) return;
        const idx = current.findIndex(item => String(getItemId(item)) === nestedId);
        if (idx === -1) return;
        if (type === "removed") {
            current.splice(idx, 1);
        } else {
            current[idx] = newValue;
        }
        return;
    }

    if (/^\d+$/.test(lastToken)) {
        const idx = Number(lastToken);
        if (!Array.isArray(current)) return;
        if (type === "removed") {
            current.splice(idx, 1);
        } else {
            current[idx] = newValue;
        }
        return;
    }

    if (type === "removed") {
        delete current[lastToken];
    } else {
        current[lastToken] = newValue;
    }
}

function getItemId(item) {
    if (!item || typeof item !== "object") return null;
    const idProps = ["id", "ID", "nombre", "name", "codigo", "code", "key"];
    for (const prop of idProps) {
        if (item[prop] !== undefined && item[prop] !== null) {
            return String(item[prop]);
        }
    }
    return null;
}

async function clearData() {
    if (confirm("¿Seguro que quieres borrar todos los datos? Esta acción no se puede deshacer.")) {
        await clearAllData();

        historias.value = {historias: []};
        metodos.value = {metodos: []};
        referencias.value = {referencias: []};
        vinculos.value = {vinculos: []};
        botVinculos.value = {sabes_algo_mas: {}, bot_vinculos: []};
        claseVinculos.value = {clase_vinculos: []};
        objetos.value = {objetos: []};
        rasgosPersonalidad.value = {rasgos_personalidad: []};
        rasgosFisicos.value = {rasgos_fisicos: []};
        rasgosClase.value = {rasgos_clase: []};
        rasgosEconomicos.value = {rasgos_economicos: []};
        afinidades.value = {afinidades: []};
        ocupaciones.value = {ocupaciones: []};
        apellidos.value = {apellidos: []};
        epilogos.value = {
            epilogos: {
                asesino: {hasganado: [], hasperdido: []},
                inocente: {
                    habeisganado: {hasacertado: [], hasfallado: []},
                    habeisperdido: {hasacertado: [], hasfallado: []},
                },
            },
        };
        escenarios.value = {escenarios: []};
        personajes.value = {personajes: []};
        colecciones.value = {colecciones: []};
        textosCoincidencias.value = {
            textos_coincidencias: {
                frases_coincidencia: [],
                coletillas_frases_coincidencia: [],
                frases_coincidencia_bot: [],
                coletillas_frases_coincidencia_bot: [],
            },
        };
        textosConjuncionesAdversativas.value = {textos_conjunciones_adversativas: {conjunciones_adversativas: []}};
        claseRasgosEconomicos.value = {clase_rasgos_economicos: []};
        claseOcupaciones.value = {clase_ocupaciones: []};

        alert("Todos los datos han sido borrados");

        location.reload();
    }
}

async function checkSynchronization() {
    try {
        const localDataRefs = {
            [KEYS.HISTORIAS]: historias,
            [KEYS.METODOS]: metodos,
            [KEYS.ESCENARIOS]: escenarios,
            [KEYS.REFERENCIAS]: referencias,
            [KEYS.VINCULOS]: vinculos,
            [KEYS.BOT_VINCULOS]: botVinculos,
            [KEYS.CLASE_VINCULOS]: claseVinculos,
            [KEYS.OBJETOS]: objetos,
            [KEYS.RASGOS_PERSONALIDAD]: rasgosPersonalidad,
            [KEYS.RASGOS_FISICOS]: rasgosFisicos,
            [KEYS.RASGOS_CLASE]: rasgosClase,
            [KEYS.RASGOS_ECONOMICOS]: rasgosEconomicos,
            [KEYS.CLASE_RASGOS_ECONOMICOS]: claseRasgosEconomicos,
            [KEYS.AFINIDADES]: afinidades,
            [KEYS.OCUPACIONES]: ocupaciones,
            [KEYS.CLASE_OCUPACIONES]: claseOcupaciones,
            [KEYS.APELLIDOS]: apellidos,
            [KEYS.EPILOGOS]: epilogos,
            [KEYS.PERSONAJES]: personajes,
            [KEYS.COLECCIONES]: colecciones,
            [KEYS.DESCARTES_TIPOS_METODOS]: descartesTiposMetodos,
            [KEYS.TEXTOS_COINCIDENCIAS]: textosCoincidencias,
            [KEYS.TEXTOS_CONJUNCIONES_ADVERSATIVAS]: textosConjuncionesAdversativas,
        };

        await checkSync(localDataRefs);
    } catch (error) {
        console.error("Error al verificar sincronización:", error);
        alert("Error al verificar sincronización: " + error.message);
    }
}

function closeSyncModal() {
    syncModalOpen.value = false;
}

async function openSyncModal() {
    loadSavesList();
    await checkSynchronization();
    syncModalOpen.value = true;
}

async function acceptServerVersion(result, shouldReload = true) {
    try {
        const config = dataTypeConfig[result.key];

        if (!config) {
            console.error("Configuración no encontrada para:", result.key);
            return;
        }

        await saveToIndexedDB(config.dbKey, result.serverData);
        await config.update(result.serverData);
        await refreshAllViews();

        const resultIndex = syncResults.value.findIndex(r => r.key === result.key);
        if (resultIndex !== -1) {
            syncResults.value[resultIndex].status = "synced";
            syncResults.value[resultIndex].message = "Sincronizado ✓";
            syncResults.value[resultIndex].differences = [];
        }

        hasConflicts.value = syncResults.value.some(r => r.status === "different");

        if (shouldReload) {
            alert("✅ Cambios aplicados correctamente.");
        }
    } catch (error) {
        console.error("Error al aceptar versión del servidor:", error);
        alert("Error al aceptar versión del servidor: " + error.message);
    }
}

async function acceptAllServerVersions() {
    if (!confirm("¿Aceptar TODAS las versiones del servidor? Esto sobrescribirá tus datos locales que tengan diferencias.")) {
        return;
    }

    try {
        const filesToUpdate = syncResults.value.filter(r => r.status === "different");

        for (const result of filesToUpdate) {
            await acceptServerVersion(result, false);
        }

        await refreshAllViews();
        alert(`✅ Se actualizaron ${filesToUpdate.length} archivos desde el servidor.`);
    } catch (error) {
        console.error("Error al aceptar todas las versiones del servidor:", error);
        alert("Error al aceptar versiones del servidor: " + error.message);
    }
}

function applySpecificChanges(localData, differences, selectedIndices, mainKey) {
    const updatedData = JSON.parse(JSON.stringify(localData));
    const dataRoot = updatedData[mainKey];

    if (Array.isArray(dataRoot)) {
        selectedIndices.forEach(index => {
            const diff = differences[index];
            if (!diff) return;
            applyChangeToData(dataRoot, diff);
        });
    } else {
        selectedIndices.forEach(index => {
            const diff = differences[index];
            if (!diff) return;
            applyChangeToObject(updatedData, diff, mainKey);
        });
    }

    return updatedData;
}

async function acceptSingleChange(changeData) {
    try {
        const {fileKey, diffIndex, fileResult} = changeData;

        console.log("Aceptando cambio individual:", {fileKey, diffIndex});

        const config = dataTypeConfig[fileKey];
        if (!config) {
            console.error("Configuración no encontrada para:", fileKey);
            return;
        }

        const updatedData = applySpecificChanges(config.ref.value, fileResult.differences, [diffIndex], config.key);

        await saveToIndexedDB(config.dbKey, updatedData);
        await config.update(updatedData);
        await refreshAllViews();

        const remainingDiffs = fileResult.differences.filter((_, idx) => idx !== diffIndex);
        const resultIndex = syncResults.value.findIndex(r => r.key === fileKey);

        if (resultIndex !== -1) {
            if (remainingDiffs.length === 0) {
                syncResults.value[resultIndex].status = "synced";
                syncResults.value[resultIndex].message = "Sincronizado ✓";
                syncResults.value[resultIndex].differences = [];
            } else {
                syncResults.value[resultIndex].status = "different";
                syncResults.value[resultIndex].message =
                    `${remainingDiffs.length} diferencia${remainingDiffs.length > 1 ? "s" : ""} encontrada${remainingDiffs.length > 1 ? "s" : ""}`;
                syncResults.value[resultIndex].differences = remainingDiffs;
            }
        }

        hasConflicts.value = syncResults.value.some(r => r.status === "different");

        alert(`✅ Cambio aceptado correctamente para ${fileResult.friendlyName}`);
    } catch (error) {
        console.error("Error al aceptar cambio individual:", error);
        alert("Error al aceptar el cambio: " + error.message);
    }
}

async function acceptSelectedChanges(changes) {
    if (!changes || changes.length === 0) {
        return;
    }

    try {
        const fileChanges = new Map();

        changes.forEach(change => {
            if (!fileChanges.has(change.fileKey)) {
                fileChanges.set(change.fileKey, {
                    fileResult: change.fileResult,
                    diffIndices: [],
                });
            }
            fileChanges.get(change.fileKey).diffIndices.push(change.diffIndex);
        });

        let filesUpdated = 0;
        for (const [fileKey, data] of fileChanges) {
            console.log(`Aplicando ${data.diffIndices.length} cambios para ${fileKey}`);

            const config = dataTypeConfig[fileKey];
            if (!config) {
                console.error("Configuración no encontrada para:", fileKey);
                continue;
            }

            const updatedData = applySpecificChanges(config.ref.value, data.fileResult.differences, data.diffIndices, config.key);

            await saveToIndexedDB(config.dbKey, updatedData);
            await config.update(updatedData);

            const remainingDiffs = data.fileResult.differences.filter((_, idx) => !data.diffIndices.includes(idx));

            const resultIndex = syncResults.value.findIndex(r => r.key === fileKey);
            if (resultIndex !== -1) {
                if (remainingDiffs.length === 0) {
                    syncResults.value[resultIndex].status = "synced";
                    syncResults.value[resultIndex].message = "Sincronizado ✓";
                    syncResults.value[resultIndex].differences = [];
                } else {
                    syncResults.value[resultIndex].status = "different";
                    syncResults.value[resultIndex].message =
                        `${remainingDiffs.length} diferencia${remainingDiffs.length > 1 ? "s" : ""} encontrada${remainingDiffs.length > 1 ? "s" : ""}`;
                    syncResults.value[resultIndex].differences = remainingDiffs;
                }
            }

            filesUpdated++;
        }

        await refreshAllViews();

        hasConflicts.value = syncResults.value.some(r => r.status === "different");

        alert(`✅ Se aplicaron cambios en ${filesUpdated} archivo(s) con un total de ${changes.length} cambio(s)`);
    } catch (error) {
        console.error("Error al aceptar cambios seleccionados:", error);
        alert("Error al aceptar cambios: " + error.message);
    }
}

function handleVisibilityChange() {
    if (!document.hidden && !isLoading.value && !isLoginPage.value) {
        console.log("👁️ Pestaña visible - Refrescando datos desde IndexedDB...");
        refreshFromIndexedDB()
            .then(() => {
                console.log("✅ Datos refrescados automáticamente");
            })
            .catch(error => {
                console.error("Error al refrescar datos:", error);
            });
    }
}

async function manualRefresh() {
    try {
        console.log("🔄 Refrescando datos manualmente...");
        await refreshFromIndexedDB();
        await checkSynchronization();
        alert("✅ Datos refrescados correctamente");
    } catch (error) {
        console.error("Error al refrescar:", error);
        alert("❌ Error al refrescar datos: " + error.message);
    }
}

onMounted(async () => {
    loadThemePreference();

    isLoading.value = true;

    try {
        currentUser.value = api.getCurrentUser();

        console.log("📥 Cargando datos del juego...");
        await loadAllGameData();
        console.log("✅ Datos cargados correctamente");

        await loadServerLock();

        lockRefreshInterval = setInterval(loadServerLock, 10000);

        document.addEventListener("visibilitychange", handleVisibilityChange);

        console.log("🔄 Verificando sincronización con el servidor...");
        await checkSynchronization();

        if (hasConflicts.value) {
            const filesWithDiffs = syncResults.value.filter(r => r.status === "different").length;
            console.warn(`⚠️ Se encontraron diferencias en ${filesWithDiffs} archivos`);
        } else {
            console.log("✅ Todos los archivos están sincronizados con el servidor");
        }
    } catch (error) {
        console.error("Error al cargar datos:", error);
        alert("Error al cargar los datos: " + error.message);
    } finally {
        isLoading.value = false;
    }
});

onBeforeUnmount(() => {
    if (lockRefreshInterval) {
        clearInterval(lockRefreshInterval);
        lockRefreshInterval = null;
    }
    document.removeEventListener("visibilitychange", handleVisibilityChange);
});
</script>
<style>
.header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 2rem 0;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.theme-toggle {
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 24px;
    transition: all 0.3s;
    backdrop-filter: blur(10px);
}

.theme-toggle:hover {
    transform: scale(1.1);
}

.sync-banner {
    padding: 12px 0;
    border-bottom: 2px solid;
    transition: all 0.3s;
}

.sync-banner-synced {
    background: rgba(46, 160, 67, 0.1);
    border-bottom-color: #2ea043;
}

.sync-banner-conflict {
    background: rgba(255, 193, 7, 0.15);
    border-bottom-color: #ffc107;
}

[data-bs-theme="dark"] .sync-banner-synced {
    background: rgba(46, 160, 67, 0.15);
}

[data-bs-theme="dark"] .sync-banner-conflict {
    background: rgba(255, 193, 7, 0.2);
}

.sync-banner-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
}

.sync-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.sync-status {
    font-weight: 500;
    font-size: 0.95rem;
}

.sync-banner-synced .sync-status {
    color: #2ea043;
}

.sync-banner-conflict .sync-status {
    color: #b86800;
}

[data-bs-theme="dark"] .sync-banner-synced .sync-status {
    color: #4ade80;
}

[data-bs-theme="dark"] .sync-banner-conflict .sync-status {
    color: #fbbf24;
}

.actions-bar {
    margin: 20px 0;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.main-tabs {
    display: flex;
    gap: 5px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.tab-btn {
    padding: 12px 24px;
    border: none;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    border-bottom: 3px solid transparent;
    transition: all 0.3s;
    text-decoration: none;
    display: inline-block;
}

@media (max-width: 576px) {
    .tab-btn {
        padding: 8px 12px;
        font-size: 14px;
    }
}

.tab-btn.active {
    border-bottom-color: #667eea;
}

.saves-section {
    background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 20px;
    border: 2px solid #667eea30;
}

.saves-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    flex-wrap: wrap;
    gap: 10px;
}

.saves-header h3 {
    margin: 0;
    color: #667eea;
}

.saves-history {
    background: white;
    border-radius: 8px;
    padding: 15px;
    margin-top: 15px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

[data-bs-theme="dark"] .saves-history {
    background: #2a2a2a;
}

.saves-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-top: 15px;
}

.save-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border-left: 4px solid #667eea;
    gap: 10px;
}

[data-bs-theme="dark"] .save-item {
    background: #1a1a1a;
}

.save-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.save-info strong {
    color: #333;
}

[data-bs-theme="dark"] .save-info strong {
    color: #fff;
}

.save-info small {
    color: #666;
    font-size: 0.85em;
}

[data-bs-theme="dark"] .save-info small {
    color: #999;
}

.save-actions {
    display: flex;
    gap: 8px;
}

.user-info {
    background: rgba(255, 255, 255, 0.2);
    padding: 8px 16px;
    border-radius: 20px;
    font-weight: 500;
    color: white;
}

.load-json-menu {
    background: linear-gradient(135deg, #667eea15 0%, #764ba215 100%);
    border: 2px solid #667eea30;
    border-radius: 12px;
    padding: 20px;
    margin: 20px 0;
}

.load-json-menu h4 {
    margin: 0 0 15px 0;
    color: #667eea;
}

.json-type-buttons {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
}

.btn-outline {
    background: white;
    border: 2px solid #667eea;
    color: #667eea;
    padding: 10px 16px;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s;
    text-align: center;
}

[data-bs-theme="dark"] .btn-outline {
    background: #2a2a2a;
    border-color: #8899ff;
    color: #8899ff;
}

.btn-outline:hover {
    background: #667eea;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

[data-bs-theme="dark"] .btn-outline:hover {
    background: #8899ff;
    color: #1a1a1a;
}

.user-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: rgba(102, 126, 234, 0.1);
    border-radius: 4px;
    font-size: 0.9em;
}

.user-badge.own-save {
    background: rgba(40, 167, 69, 0.1);
    color: #28a745;
}

[data-bs-theme="dark"] .user-badge.own-save {
    color: #4ade80;
}

.owner-badge {
    color: #28a745;
    font-weight: bold;
}

.save-item.other-user {
    opacity: 0.85;
    border-left-color: #999;
}

/* Estilos de bloqueo del servidor */
.btn-warning {
    background: #ffc107;
    border-color: #ffc107;
    color: #000;
}

.btn-warning:hover:not(:disabled) {
    background: #e0a800;
    border-color: #d39e00;
}

.btn-danger {
    background: #dc3545;
    border-color: #dc3545;
    color: white;
}

.btn-danger:hover:not(:disabled) {
    background: #c82333;
    border-color: #bd2130;
}

.text-danger {
    color: #dc3545 !important;
    font-size: 0.85rem;
}

[data-bs-theme="dark"] .text-danger {
    color: #ff6b6b !important;
}

/* Estilos para toast de sincronización */
#sync-toast {
    min-width: 300px;
}

#sync-toast .toast-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
}

#sync-toast .toast-header .btn-close {
    background-color: transparent;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e");
    opacity: 0.8;
}

#sync-toast .toast-header .btn-close:hover {
    opacity: 1;
    transform: scale(1.1);
}

#sync-toast .toast-body {
    background: white;
    color: #333;
}

[data-bs-theme="dark"] #sync-toast .toast-body {
    background: #2a2a2a;
    color: #e9ecef;
}

[data-bs-theme="dark"] #sync-toast .toast-header {
    background: linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%);
}
</style>
